import { LitElement, css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators';
import { login, saveEmail, upgradeToken } from '../../helpers/api/account-api';

import { LoginOrigin } from '../../types';
import { StateController } from '../../helpers/state/state-management/state-controller';
import accountInfo, {} from '../../helpers/state/account-info';
import { getMainPage } from '../../helpers/page-name';
import { getQueryStringParameters } from '../../helpers/urls';
import pointCache from '../../helpers/state/point-cache';
import { setLocalStorage } from '../../helpers/utils';
import topicData from '../../helpers/state/topic-data';
import { updateTopicAndPointsWithEmail } from '../../helpers/api/topics-api';

@customElement('enter-email-component')
export class EnterEmailComponent extends LitElement {
    static amplitude_callsite: string = 'Enter Email Modal';

    @property({ type: String })
    emailAddress?: string;



    @property({ type: Boolean })
    updateEmail: boolean = false;

    @property({ type: Object })
    facebookAuth: Object = { scope: 'email' };

    @property({ type: Boolean })
    show_one_time_code: boolean = false;

    @property({ type: String })
    one_time_code?: string;

    @property({ type: Boolean })
    requireVerification: boolean = false;

    @state()
    isLoading: boolean = false;

    @state()
    errorMessage?: string;

    private readonly bindPointCacheState = new StateController(this, pointCache);
    private readonly bindAccountInfo = new StateController(this, accountInfo);

    handleChangeEmailAddress(e: any) {
        this.emailAddress = e.detail.value;
    }

    handleChangeOneTimeCode(e: any) {
        this.one_time_code = e.detail.value;
    }

    private async handleNewEmail() {
        if (accountInfo.isAuthenticated) {
            await accountInfo.logout();
        }
        this.show_one_time_code = false;
        this.requestUpdate();
    }

    private async handleSubmit() {
        const email = this.emailAddress ?? '';

        if (!this.requireVerification) {
            this.emailAddress = email;
            saveEmail(email);
            this.emailUpdated();
        } else {
            await getMainPage().errorToastElement.tryOrErrorToast(async () => {
                await login(EnterEmailComponent.amplitude_callsite, email);
                this.show_one_time_code = true;
                this.emailUpdated();
            }, 'An error has occurred.  Context: could not request an authentication token.');
        };
    }

    private emailUpdated() {
        dispatchEvent(new CustomEvent('email-updated', { detail: { email: this.emailAddress } }));
    }

    private async handleSubmitOneTimeCode(organization_id?: string) {
        const email = this.emailAddress ?? '';
        const one_time_code = this.one_time_code ?? '';

        await getMainPage().errorToastElement.tryOrErrorToast(async () => {
            // Authenticate in the backend with one time code
            await upgradeToken(
                EnterEmailComponent.amplitude_callsite,
                email,
                one_time_code,
                undefined,
                LoginOrigin.SigninModule
            );
            setLocalStorage('email', email);
            accountInfo.email = email;
            this.requestUpdate();
        }, 'An error has occurred.  Context: could not upgrade the authentication token.');
    }


    private async postUpgradeUpdates() {
        // Instantiate account cache
        await accountInfo.instantiateAccountData();

        this.handleReirectAfterUpgrade();
    }

    private handleReirectAfterUpgrade() {
        if (window.location.hash.startsWith('#login')) {
            const queryString = getQueryStringParameters();
            const callback_url = queryString.get('callback_url');
            if (callback_url) {
                window.location.assign(callback_url);
            } else {
                window.location.assign('/#home');
            }
        } else {
            this.dispatchEvent(new CustomEvent('close'));
        }
    }

    private async updateMapWithEmail(email: string) {
        const queryString = getQueryStringParameters();
        const topic_id = queryString.get('topic_id') ?? topicData.topicId;
        const topic_key = queryString.get('topic_key') ?? topicData.topicKey;

        if (topic_id && topic_key) {
            await updateTopicAndPointsWithEmail(
                EnterEmailComponent.amplitude_callsite,
                topic_id, email,
                topic_key,
                pointCache.allPoints.length,
            );
        }
    }

    private gotoHome() {
        window.location.assign('https://proxi.co');
    }

    renderFirstScreen() {
        return html`
            <form-input
                id="name"
                type="email"
                label="Email Address"
                placeholder="Enter your email"
                value="${this.emailAddress ?? ''}"
                @change="${this.handleChangeEmailAddress}">
            </form-input>
            ${this.requireVerification ? html`
                <div class="flexContainer">
                    <span>You will receive a one time code that you will enter here.</span>
                </div>
                <form-button
                    type="submit"
                    id="signinBtn"
                    @click="${this.handleSubmit}"
                    ?fullWidth=${true}>
                    Send My One Time Code
                </form-button>
            ` : html`
                <form-button
                    type="submit"
                    id="signinBtn"
                    @click="${this.handleSubmit}"
                    ?fullWidth=${true}>
                    Continue
                </form-button>
            `}
        `;
    }

    renderSecondScreen() {
        return html`
            <h1 class="modal-title">Email Sent</h1>
            <p class="code-description">Check your email <b>${this.emailAddress}</b> and enter the 6 digit code here:</p>
            <form-input
                id="name"
                type="string"
                label="Verification Code"
                placeholder="000000"
                value="${this.one_time_code ?? ''}"
                @change="${this.handleChangeOneTimeCode}">
            </form-input>
            <form-button
                type="submit"
                id="signinBtn"
                @click="${this.handleSubmitOneTimeCode}"
                ?fullWidth=${true}>
                Confirm
            </form-button>
            <div class="tryAgainLink">
                Didn't receive the email? Check your spam folder or <span class="click-resend" @click=${this.handleSubmit}>click to resend</span>.
            </div>
            <div class="tryAgainLink" @click=${this.handleNewEmail}>
                <mwc-icon>arrow_back</mwc-icon>
                <span>Try Another Email</span>
            </div>
        `;
    }

    render() {
        return html`
            <div class="flexContainer alignCenter signinModalContent">
                ${this.show_one_time_code ?
                    this.renderSecondScreen()
                    : this.renderFirstScreen()}
            </div>
            <error-toast id="errorToast"></error-toast>
        `;
    }

    static get styles() {
        return css`

            .signinModalContent {
                padding:1rem;
            }

            .flexContainer {
                display: flex;
                flex-direction: column;
            }

            .alignCenter {
                align-items: center;
            }

            .justifyCenter {
                justify-content: center;
            }

            .justifySpaceBetween {
                justify-content: space-between;
            }

            .rightSection {
                flex: 1;
                padding: 2rem;
            }

            .textCenter {
                text-align: center;
            }

            .modal-title {
                font-size: 1rem;
                margin: 0;
                line-height: 1.4;
            }

            .login-description {
                font-size: 0.8rem;
                margin: 1rem 0;
            }

            .code-description {
                font-size: 1rem;
                margin-top: 0.5rem;
                margin-bottom: 1.5rem;
                line-height: 1.5;
            }

            .dividerSection {
                margin: 1rem 0;
            }

            .dividerSection div {
                padding: 0 1rem;
            }

            .dividerSection hr {
                width: 100%;
                border: none;
                background: var(--theme-border-color);
                height: 1px;
            }

            .socialButtonWrapper {
                margin-bottom: 1rem;
                display: flex;
                flex-direction: row;
                gap: 1rem;
            }

            #signinBtn {
                display: block;
                margin-top: 1rem;
                --btn-padding-x: 2rem;
            }

            .tryAgainLink {
                cursor: pointer;
                text-decoration: none;
                font-weight: 500;
                margin-top: 1rem;
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 0.5rem;
            }

            .teamsInfo {
                text-decoration: none;
                font-weight: 500;
                margin-top: 1rem;
                display: flex;
                align-items: center;
                text-align: center;
                gap: 0.5rem;
            }

            mwc-icon {
                font-size: 1rem;
            }

            .click-resend {
                color: var(--main-invalid-color);
                font-weight: 600;
            }

            .accounts-list {
                margin: 1rem 0;
                display: inline-flex;
                flex-wrap: wrap;
                justify-content: center;
                gap: 10px;
            }

            .account-card {
                padding: 0.5rem;
                text-align: center;
                cursor: pointer;
                font-size: 18px;
                font-weight: 700;
                border-radius: 6px;
                box-shadow: 0px 0px 0.5rem rgba(0, 0, 0, 0.25);
                display: flex;
                flex-direction: column;
                align-items: center;
            }

            form-button svg {
                margin-right: 0.75rem;
                fill: var(--main-primary-color);
                transition: fill 0.2s ease-in-out;
            }

            form-button:hover svg {
                fill: var(--theme-white-color);
            }

            @media (min-width: 961px) {
                .desktopOnly {
                    display: block;
                }
            }

            @media (max-width: 960px) {
                .desktopOnly {
                    display: none;
                }

                .signinModalContent {
                    flex-direction: column;
                }
            }

            @media (max-width: 550px) {
                .rightSection {
                    padding-left: 0;
                    padding-right: 0;
                }
            }
        `;
    }
}

